import { useCallback } from 'react'

import { ComplaintsDataCollectionItemFragment } from 'generated/resources/graphql'
import { ComplaintDetailDocument, UpdateComplaintStatusDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  getStatusOptions,
  getStatusOptionsForModal,
} from 'modules/complaints/lib/utils/complaintStatus'

interface UseComplaintStatusReturn {
  updateComplaintStatus: (complaintId: number, status: string) => Promise<{ success: boolean }>
  getStatusOptions: (
    complaint: ComplaintsDataCollectionItemFragment
  ) => Array<{ value: string; name: string }>
  getStatusOptionsForModal: (currentStatus: string) => Array<{ label: string; value: string }>
}

export const useComplaintStatus = (): UseComplaintStatusReturn => {
  const [updateComplaintStatus] = useMutation(UpdateComplaintStatusDocument, {
    extractorFn: (data) => data.complaintsMutations?.updateStatusComplaintsComplaint,
    refetchQueries: [ComplaintDetailDocument],
  })

  const updateComplaintStatusMutation = useCallback(
    async (complaintId: number, status: string) => {
      try {
        const result = await updateComplaintStatus({
          variables: {
            complaintId,
            status,
          },
        })

        const handler = handleResult(result, (_errors) => {})

        if (handler.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        }
        return handler
      } catch (error) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
        return { success: false }
      }
    },
    [updateComplaintStatus]
  )

  const getStatusOptionsForComplaint = useCallback(
    (complaint: ComplaintsDataCollectionItemFragment) => {
      return getStatusOptions(complaint.status)
    },
    []
  )

  const getStatusOptionsForModalCallback = useCallback((currentStatus: string) => {
    return getStatusOptionsForModal(currentStatus)
  }, [])

  return {
    updateComplaintStatus: updateComplaintStatusMutation,
    getStatusOptions: getStatusOptionsForComplaint,
    getStatusOptionsForModal: getStatusOptionsForModalCallback,
  }
}
